/**
 * Copy and presentation rules for log entries.
 *
 * All the Japanese microcopy the screens render lives here rather than being
 * inlined at call sites, so the two languages stay editable together and the
 * activity feed and timeline can never drift out of phrasing with each other.
 *
 * Voice, per the design system: warm, plain-spoken です／ます, kana-forward,
 * low pressure. Never machine-translated between the two languages.
 */

import type { Entry, EntryType, FeedSource, ParentRole } from './types';
import { formatDurationJa, formatTime } from './time';

// ---------------------------------------------------------------- entry types

export const ENTRY_TYPES: { key: EntryType; label: string; labelEn: string; icon: string }[] = [
  { key: 'feed', label: '授乳', labelEn: 'Feed', icon: 'Milk' },
  { key: 'diaper', label: 'おむつ', labelEn: 'Diaper', icon: 'Baby' },
  { key: 'sleep', label: 'ねんね', labelEn: 'Sleep', icon: 'Moon' },
  { key: 'solid', label: '離乳食', labelEn: 'Solids', icon: 'Apple' },
];

export function entryTypeIcon(type: EntryType): string {
  return ENTRY_TYPES.find((t) => t.key === type)?.icon ?? 'Baby';
}

/**
 * Tile colours per entry type, lifted from the prototype's timeline rows:
 * sleep reads as warm sand, diapers as sky, anything eaten as sage.
 */
export function entryTone(type: EntryType): { bg: string; fg: string } {
  switch (type) {
    case 'sleep':
      return { bg: 'var(--sand-200)', fg: 'var(--sand-700)' };
    case 'diaper':
      return { bg: 'var(--sky-100)', fg: 'var(--sky-600)' };
    default:
      return { bg: 'var(--sage-100)', fg: 'var(--sage-600)' };
  }
}

// ---------------------------------------------------------------- feeds

export const FEED_SOURCES: { key: FeedSource; label: string; labelEn: string }[] = [
  { key: 'right', label: '右', labelEn: 'Right breast' },
  { key: 'left', label: '左', labelEn: 'Left breast' },
  { key: 'formula', label: 'ミルク', labelEn: 'Formula' },
  { key: 'expressed', label: '搾乳', labelEn: 'Expressed' },
];

/** 右 and 左 are breastfeeding, so they are timed; the other two are measured. */
export function isBreast(source: FeedSource): boolean {
  return source === 'right' || source === 'left';
}

export function feedUnit(source: FeedSource): string {
  return isBreast(source) ? '分' : 'ml';
}

/** Minutes step by 1, millilitres by 10. */
export function feedStep(source: FeedSource): number {
  return isBreast(source) ? 1 : 10;
}

export function feedQtyLabel(source: FeedSource): string {
  return isBreast(source) ? '授乳時間 · Duration' : '量 · Amount';
}

/**
 * Sensible starting value when you switch source. Breastfeeding starts at 10
 * minutes; a bottle starts at 120 ml. (The prototype originally offered "120"
 * for both, which read oddly as minutes.)
 */
export function feedDefaultAmount(source: FeedSource): number {
  return isBreast(source) ? 10 : 120;
}

export function feedSourceLabel(source: FeedSource): string {
  switch (source) {
    case 'right':
      return '母乳（右）';
    case 'left':
      return '母乳（左）';
    case 'formula':
      return 'ミルク';
    case 'expressed':
      return '搾乳';
  }
}

// ---------------------------------------------------------------- diapers

export const DIAPER_KINDS = [
  { key: 'pee', label: 'おしっこ', labelEn: 'Pee', icon: 'Droplet', accent: 'sky' },
  { key: 'poop', label: 'うんち', labelEn: 'Poop', icon: 'CircleDot', accent: 'amber' },
] as const;

export function diaperContentsLabel(pee: boolean, poop: boolean): string {
  const parts: string[] = [];
  if (pee) parts.push('おしっこ');
  if (poop) parts.push('うんち');
  return parts.length ? parts.join('・') : '記録';
}

// ---------------------------------------------------------------- entry copy

/** Row title in the timeline, e.g. "授乳 · ミルク" or "ねんね · お昼寝". */
export function entryTitle(entry: Entry): string {
  switch (entry.type) {
    case 'feed':
      return `授乳 · ${feedSourceLabel(entry.source)}`;
    case 'diaper':
      return `おむつ · ${diaperContentsLabel(entry.pee, entry.poop)}`;
    case 'sleep':
      return `ねんね · ${sleepKindLabel(entry.at)}`;
    case 'solid':
      return '離乳食';
  }
}

/** お昼寝 during the day, 夜 overnight — matching the prototype's two rows. */
export function sleepKindLabel(start: Date): string {
  const h = start.getHours();
  return h >= 19 || h < 6 ? '夜' : 'お昼寝';
}

/** Right-hand value in the timeline: "15分", "120ml", "1時間30分", "記録中". */
export function entryAmountLabel(entry: Entry): string {
  switch (entry.type) {
    case 'feed':
      return `${entry.amount}${feedUnit(entry.source)}`;
    case 'diaper':
      return '';
    case 'sleep':
      if (!entry.endAt) return '記録中';
      return formatDurationJa((entry.endAt.getTime() - entry.at.getTime()) / 60_000);
    case 'solid':
      return `${entry.grams}g`;
  }
}

/** Time column in the timeline. Sleep shows its range; an open nap shows "〜". */
export function entryTimeLabel(entry: Entry): string {
  if (entry.type === 'sleep') {
    return `${formatTime(entry.at)}–${entry.endAt ? formatTime(entry.endAt) : ''}`;
  }
  return formatTime(entry.at);
}

/**
 * The fields the copy helpers actually read.
 *
 * Narrower than `Entry` so a not-yet-saved entry (no id, no createdAt) can be
 * described without inventing placeholder values for the stored metadata. A
 * full `Entry` satisfies it structurally.
 */
export type EntryCopyInput =
  | { type: 'feed'; source: FeedSource; amount: number }
  | { type: 'diaper'; pee: boolean; poop: boolean }
  | { type: 'sleep'; at: Date; endAt: Date | null }
  | { type: 'solid'; grams: number };

/**
 * The sentence stored on the activity feed, e.g. "授乳 · ミルク 120ml".
 * Written once at save time — see the note on `Activity` in types.ts.
 */
export function entryActivityAction(entry: EntryCopyInput): string {
  switch (entry.type) {
    case 'feed':
      return `授乳 · ${feedSourceLabel(entry.source)} ${entry.amount}${feedUnit(entry.source)}`;
    case 'diaper':
      return `おむつ · ${diaperContentsLabel(entry.pee, entry.poop)}`;
    case 'sleep':
      return `ねんね · ${formatTime(entry.at)}〜${entry.endAt ? formatTime(entry.endAt) : '（記録中）'}`;
    case 'solid':
      return `離乳食 · ${entry.grams}g`;
  }
}

/** Feed sentence for a corrected entry, showing the values it now has. */
export function entryEditAction(entry: EntryCopyInput): string {
  return `${entryActivityAction(entry)} に修正`;
}

/** Feed sentence for a removed entry, showing what went. */
export function entryDeleteAction(entry: EntryCopyInput): string {
  return `${entryActivityAction(entry)} を削除`;
}

/** Short confirmation shown in the toast under 記録しました. */
export function entryToastMessage(entry: EntryCopyInput): string {
  switch (entry.type) {
    case 'feed':
      return `${feedSourceLabel(entry.source)} ${entry.amount}${feedUnit(entry.source)}`;
    case 'diaper':
      return diaperContentsLabel(entry.pee, entry.poop);
    case 'sleep':
      return entry.endAt
        ? `${formatTime(entry.at)}〜${formatTime(entry.endAt)}`
        : `${formatTime(entry.at)}〜（記録中）`;
    case 'solid':
      return `${entry.grams}g`;
  }
}

// ---------------------------------------------------------------- parents

/** Mom is sky, dad is sage — the colour pairing the whole handoff UI relies on. */
export function roleTone(role: ParentRole): {
  solid: string;
  bg: string;
  fg: string;
  border: string;
  text: string;
} {
  return role === 'mom'
    ? {
        solid: 'var(--sky-500)',
        bg: 'var(--sky-100)',
        fg: 'var(--sky-600)',
        border: 'var(--sky-400)',
        text: 'var(--sky-700)',
      }
    : {
        solid: 'var(--sage-500)',
        bg: 'var(--sage-100)',
        fg: 'var(--sage-600)',
        border: 'var(--sage-400)',
        text: 'var(--sage-700)',
      };
}

export function growthKindLabel(kind: 'weight' | 'height'): {
  latestLabel: string;
  unit: string;
} {
  return kind === 'weight'
    ? { latestLabel: 'いまの体重 · Weight', unit: 'kg' }
    : { latestLabel: 'いまの身長 · Height', unit: 'cm' };
}
